import AddIcon from '@mui/icons-material/Add';
import React from 'react';
import { superAdminTooltips } from '../../../shared/config/actionHelpCatalog';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import HelpTooltipTrigger from '../../../shared/ui/common/HelpTooltipTrigger';
import type {
  AdminLicenseBulkAction,
  AdminLicenseBulkOutcome,
  AdminLicenseDto,
} from '../types';
import { LICENSE_TYPE_LABELS } from '../types';
import { formatAdminUtcDate, getAdminUtcDaysUntil } from '../lib/adminDates';
import { getAdminApiError } from '../lib/adminApiErrors';
import {
  formatLicenseBulkResult,
  formatExportElementQuota,
  getLicenseExpirySortValue,
  getLicenseOccupancyRatio,
  getLicenseTypeLabel,
} from '../lib/adminLicenses';
import {
  AdminButton,
  AdminDataTable,
  ConfirmDialog,
  OccupancyMeter,
  StatusPill,
  useAdminToast,
  type AdminDataTableBulkContext,
  type AdminDataTableColumn,
  type AdminDataTableFilterValues,
  type AdminDataTableRowId,
  type AdminDataTableSortState,
} from './components';
import './styles/adminLicenses.css';

interface LicenseTableProps {
  licenses: readonly AdminLicenseDto[];
  onAdd: () => void;
  onOpen?: (license: AdminLicenseDto) => void;
  onBulkAction?: (
    action: AdminLicenseBulkAction,
    ids: number[]
  ) => Promise<AdminLicenseBulkOutcome>;
  filterValues?: AdminDataTableFilterValues;
  onFilterValuesChange?: (filters: AdminDataTableFilterValues) => void;
  sort?: AdminDataTableSortState;
  onSortChange?: (sort: AdminDataTableSortState) => void;
  selectedIds?: readonly AdminDataTableRowId[];
  onSelectedIdsChange?: (ids: readonly AdminDataTableRowId[]) => void;
  busy?: boolean;
  addDisabled?: boolean;
  now?: Date;
}

const TypeFilterOptions = Object.entries(LICENSE_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const StatusFilterOptions = [
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'expired', label: 'Expired' },
];

const statusTone = (status: AdminLicenseDto['status']) => {
  if (status === 'active') {
    return 'success' as const;
  }
  return status === 'suspended' ? 'warning' as const : 'critical' as const;
};

const titleCase = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

const ColumnHelp: React.FC<{
  label: string;
  help: Parameters<typeof HelpTooltipTrigger>[0]['help'];
}> = ({ label, help }) => (
  <HelpTooltipTrigger label={label} help={help} placement="bottom-start" />
);

const createColumns = (now: Date): AdminDataTableColumn<AdminLicenseDto>[] => [
  {
    id: 'activationKey',
    header: 'Activation key',
    ariaLabel: 'Activation key',
    headerAccessory: <ColumnHelp label="Activation key" help={superAdminTooltips.licenseActivationKeyColumn} />,
    accessor: (license) => license.activationKey,
    render: (license) => <span className="admin-license-key" title={license.activationKey}>{license.activationKey}</span>,
    filter: { type: 'text', placeholder: 'Filter key' },
    width: '2fr',
  },
  {
    id: 'company',
    header: 'Company',
    headerAccessory: <ColumnHelp label="Company" help={superAdminTooltips.licenseCompanyColumn} />,
    accessor: (license) => license.companyName,
    render: (license) => <strong className="admin-license-company">{license.companyName || '\u2014'}</strong>,
    filter: { type: 'text', placeholder: 'Filter company' },
    width: '1.3fr',
  },
  {
    id: 'type',
    header: 'Type',
    headerAccessory: <ColumnHelp label="Type" help={superAdminTooltips.licenseTypeColumn} />,
    accessor: (license) => license.type,
    filterValue: (license) => String(license.type),
    render: (license) => <StatusPill tone="line">{getLicenseTypeLabel(license.type)}</StatusPill>,
    filter: { type: 'select', options: TypeFilterOptions, allLabel: 'All types' },
    width: '.8fr',
  },
  {
    id: 'instances',
    header: 'Instances',
    headerAccessory: <ColumnHelp label="Instances" help={superAdminTooltips.licenseInstancesColumn} />,
    accessor: (license) => license.machinesCount,
    sortValue: getLicenseOccupancyRatio,
    render: (license) => (
      <OccupancyMeter
        used={license.machinesCount}
        maximum={license.allowedInstances}
        label={`Instance occupancy for ${license.activationKey}`}
      />
    ),
    width: '1.1fr',
  },
  {
    id: 'registrations',
    header: 'Registrations',
    ariaLabel: 'Registrations',
    headerAccessory: <ColumnHelp label="Registrations" help={superAdminTooltips.licenseRegistrationsColumn} />,
    accessor: (license) => license.registeredUsersCount,
    numeric: true,
    width: '.6fr',
  },
  {
    id: 'exportElements',
    header: 'Export elements',
    headerAccessory: <ColumnHelp label="Export elements" help={superAdminTooltips.licenseExportElementsColumn} />,
    accessor: (license) => license.remainingExportElements ?? Number.POSITIVE_INFINITY,
    render: (license) => (
      <span className={`admin-license-quota${license.remainingExportElements === 0 ? ' admin-license-quota--exhausted' : ''}`}>
        {formatExportElementQuota(license.remainingExportElements)}
      </span>
    ),
    numeric: true,
    width: '1fr',
  },
  {
    id: 'status',
    header: 'Status',
    headerAccessory: <ColumnHelp label="Status" help={superAdminTooltips.licenseStatusColumn} />,
    accessor: (license) => license.status,
    render: (license) => <StatusPill tone={statusTone(license.status)}>{titleCase(license.status)}</StatusPill>,
    filter: { type: 'select', options: StatusFilterOptions, allLabel: 'All statuses' },
    width: '.8fr',
  },
  {
    id: 'expires',
    header: 'Expires',
    headerAccessory: <ColumnHelp label="Expires" help={superAdminTooltips.licenseExpiresColumn} />,
    accessor: (license) => license.dueTime,
    sortValue: getLicenseExpirySortValue,
    render: (license) => {
      const days = license.dueTime ? getAdminUtcDaysUntil(license.dueTime, now) : 0;
      return (
        <span className="admin-license-expiry">
          <span>{formatAdminUtcDate(license.dueTime)}</span>
          {days >= 1 && days <= 30 && <StatusPill tone="warning">{days}d</StatusPill>}
        </span>
      );
    },
    numeric: true,
    width: '.9fr',
  },
];

interface PendingBulkDelete {
  ids: number[];
  deselectIds: AdminDataTableBulkContext<AdminLicenseDto>['deselectIds'];
}

const LicenseTableHeader: React.FC<{
  addDisabled: boolean;
  count: number;
  onAdd: () => void;
}> = ({ addDisabled, count, onAdd }) => (
  <header className="admin-licenses__header">
    <h2>Licenses <span aria-hidden="true">{count}</span></h2>
    <ActionHelpTooltip help={superAdminTooltips.addLicense} placement="bottom">
      <AdminButton variant="primary" disabled={addDisabled} onClick={onAdd}>
        <AddIcon aria-hidden="true" fontSize="small" /> Add license
      </AdminButton>
    </ActionHelpTooltip>
  </header>
);

const LicenseTable: React.FC<LicenseTableProps> = ({
  licenses,
  onAdd,
  onOpen,
  onBulkAction,
  filterValues,
  onFilterValuesChange,
  sort,
  onSortChange,
  selectedIds,
  onSelectedIdsChange,
  busy = false,
  addDisabled = false,
  now = new Date(),
}) => {
  const { showToast } = useAdminToast();
  const [pendingDelete, setPendingDelete] = React.useState<PendingBulkDelete | null>(null);
  const columns = React.useMemo(() => createColumns(now), [now]);

  const runBulk = async (
    action: AdminLicenseBulkAction,
    ids: number[],
    deselectIds: PendingBulkDelete['deselectIds']
  ) => {
    if (!onBulkAction) {
      return;
    }
    try {
      const result = await onBulkAction(action, ids);
      deselectIds(result.succeeded);
      const refreshMessage = result.refreshError
        ? ` Canonical refresh failed (${result.refreshError.code}).`
        : '';
      showToast(`${formatLicenseBulkResult(action, result)}${refreshMessage}`, {
        tone: result.failed.length > 0 || result.refreshError ? 'warning' : 'success',
      });
    } catch (error) {
      const failure = getAdminApiError(error, 'license_bulk_failed');
      showToast(`Bulk action failed (${failure.code}).`, { tone: 'critical' });
    }
  };

  const bulkSlot = ({ selectedRows, deselectIds }: AdminDataTableBulkContext<AdminLicenseDto>) => {
    const ids = selectedRows.map((license) => license.id);
    return (
      <>
        <span className="admin-license-bulk-count"><strong>{ids.length}</strong> selected</span>
        <AdminButton disabled={busy || !onBulkAction} onClick={() => void runBulk('extend', ids, deselectIds)}>Extend 1 year</AdminButton>
        <AdminButton disabled={busy || !onBulkAction} onClick={() => void runBulk('suspend', ids, deselectIds)}>Suspend</AdminButton>
        <AdminButton variant="danger" disabled={busy || !onBulkAction} onClick={() => setPendingDelete({ ids, deselectIds })}>Delete</AdminButton>
      </>
    );
  };

  return (
    <section
      className="admin-licenses"
      data-ready={String(!busy)}
      data-testid="admin-licenses"
    >
      <LicenseTableHeader addDisabled={addDisabled} count={licenses.length} onAdd={onAdd} />
      <AdminDataTable
        ariaLabel="Licenses"
        rows={licenses}
        columns={columns}
        getRowId={(license) => license.id}
        getRowLabel={(license) => license.activationKey}
        selectable
        onRowActivate={onOpen}
        bulkSlot={bulkSlot}
        emptyState="Nothing matches the current filters"
        filterValues={filterValues}
        onFilterValuesChange={onFilterValuesChange}
        sort={sort}
        onSortChange={onSortChange}
        selectedIds={selectedIds}
        onSelectedIdsChange={onSelectedIdsChange}
      />
      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete selected licenses?"
        description={`${pendingDelete?.ids.length ?? 0} selected license(s) will be deleted and their active machines revoked. This cannot be undone.`}
        confirmLabel={`Delete ${pendingDelete?.ids.length ?? 0} licenses`}
        confirmDisabled={busy}
        closeOnConfirm={false}
        onConfirm={async () => {
          if (!pendingDelete) {
            return;
          }
          await runBulk('delete', pendingDelete.ids, pendingDelete.deselectIds);
          setPendingDelete(null);
        }}
        onClose={() => setPendingDelete(null)}
      />
    </section>
  );
};

export default LicenseTable;
